import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useIntl } from "react-intl";

import { AiUsageRecordRow } from "../types/MeGenTypes";
import type { RecordCostInfo } from "./aiModelCost";
import { formatMetric, formatMoney } from "./aiUsagePivot";
import { knownTaskCost, knownTaskValue, taskCurrencyOf } from "./ffmpegUsage";

const FfmpegUsageSummary: React.FC<{ records: AiUsageRecordRow[]; costInfoOf: (row: AiUsageRecordRow) => RecordCostInfo }> = ({ records, costInfoOf }) => {
  const intl = useIntl();
  const summary = useMemo(() => {
    const ffmpegRows = records.filter((row) => !!row.ffmpeg_task);
    const tasks = ffmpegRows.map((row) => row.ffmpeg_task!);
    const costResults = ffmpegRows.map((row) => costInfoOf(row).result);
    const costs = new Map<string, number>();
    tasks.forEach((task, index) => {
      if (knownTaskCost(task)) costs.set(taskCurrencyOf(task), (costs.get(taskCurrencyOf(task)) || 0) + task.allocated_cost!);
      else if (costResults[index].calculatedAllocationCount) costs.set("USD", (costs.get("USD") || 0) + costResults[index].cost);
    });
    return {
      count: tasks.length,
      allocated: tasks.reduce((sum, task) => sum + (knownTaskValue(task.allocated_instance_seconds) ? task.allocated_instance_seconds : 0), 0),
      compute: tasks.reduce((sum, task) => sum + (knownTaskValue(task.gpu_compute_seconds) ? task.gpu_compute_seconds : 0), 0),
      missingCost: tasks.filter((task, index) => !knownTaskCost(task) && !costResults[index].calculatedAllocationCount).length,
      missingTime: tasks.filter((task) => !knownTaskValue(task.allocated_instance_seconds)).length,
      active: tasks.filter((task) => task.status === "pending" || task.status === "running").length,
      costs: Array.from(costs.entries()).sort(([a], [b]) => a.localeCompare(b)),
    };
  }, [records, costInfoOf]);
  if (!summary.count) return null;
  return <Box role="status" aria-label={intl.formatMessage({ id: "megen.cost.ffmpegSummary" })} sx={{ mb: 1, p: 1.5, backgroundColor: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "0.5rem" }}>
    <Typography variant="body2">{intl.formatMessage({ id: "megen.cost.ffmpegUsage" }, { count: summary.count, allocated: formatMetric(summary.allocated), compute: formatMetric(summary.compute) })}</Typography>
    {!!summary.costs.length && <Typography variant="body2">{intl.formatMessage({ id: "megen.cost.ffmpegKnownCosts" }, { costs: summary.costs.map(([currency, amount]) => currency === "USD" ? `${formatMoney(amount)} USD` : `${formatMetric(amount)} ${currency}`).join(" · ") })}</Typography>}
    {(!!summary.missingCost || !!summary.missingTime) && <Typography variant="body2" color="warning.main">{intl.formatMessage({ id: "megen.cost.ffmpegUnknown" }, { costs: summary.missingCost, times: summary.missingTime })}</Typography>}
    {!!summary.active && <Typography variant="caption" display="block">{intl.formatMessage({ id: "megen.cost.ffmpegActive" }, { count: summary.active })}</Typography>}
    <Typography variant="caption" display="block">{intl.formatMessage({ id: "megen.cost.ffmpegScope" })}</Typography>
  </Box>;
};

export default FfmpegUsageSummary;
